import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';

// window.history.pushState() changes window.location.href but does not trigger Next.js router events
// router.asPath doesn't update automatically
// The component needs the current URL to determine which nav item is active
// Without this, clicking sticky nav items on the same page wouldn't update the active state because router.asPath wouldn't change.
const useRouterUrlSync = () => {
  const router = useRouter();
  const routerAsPathRef = useRef(router.asPath);

  const [currentUrl, setCurrentUrl] = useState<string>(() => {
    if (typeof window !== 'undefined' && window.location?.href) {
      return window.location.href;
    }
    return router.asPath || '';
  });

  useEffect(() => {
    routerAsPathRef.current = router.asPath;
  }, [router.asPath]);

  useEffect(() => {
    const updateUrl = () => {
      if (typeof window !== 'undefined' && window.location?.href) {
        setCurrentUrl(window.location.href);
      } else {
        setCurrentUrl(routerAsPathRef.current || '');
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('popstate', updateUrl);
    }
    const handleRouteChange = () => {
      updateUrl();
    };
    router.events?.on('routeChangeComplete', handleRouteChange);

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('popstate', updateUrl);
      }
      router.events?.off('routeChangeComplete', handleRouteChange);
    };
  }, [router]);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.location?.href) {
      const urlFromWindow = window.location.href;
      setCurrentUrl((prev) => (prev !== urlFromWindow ? urlFromWindow : prev));
    } else if (router.asPath) {
      setCurrentUrl((prev) => (prev !== router.asPath ? router.asPath : prev));
    }
  }, [router.asPath]);

  return { currentUrl, setCurrentUrl };
};

export default useRouterUrlSync;
